import React, { useEffect, useRef, useState } from 'react';
import { Sticky, StickyContainer } from 'react-sticky';

import styled from 'styled-components';

import { SmallTab } from '@components/common/Tab';
import { PartnerBreadcrumb } from '@components/partners/Breadcrumb';
import PartnerFAQ from '@components/partners/PartnerFAQ';
import PartnerInfo from '@components/partners/PartnerInfo';
import PartnerReview from '@components/partners/PartnerReview';
import { RelatedServices } from '@components/partners/RelatedServices';
import { Container } from '@components/parts/Container';
import HeadMeta from '@components/parts/HeadMeta';
import { SEOStructuredDataComponent } from '@components/parts/SEOStructuredData';

import { throttle } from '@utils/throttle';

import { FeedbackSummaryDto, Partner as PartnerType } from '@customTypes/Partner';
import { PartnerPageTemplate } from '@customTypes/PartnerPageTemplate';
import { Regions } from '@customTypes/Region';
import { Services } from '@customTypes/Service';

import PartnerCTA from './PartnerCTA';
import PartnerName from './PartnerName';

interface Props {
  partner: PartnerType;
  services: Services;
  regions: Regions;
  feedbackSummary: FeedbackSummaryDto;
  template?: PartnerPageTemplate;
}

const Wrapper = styled.div`
  margin-top: 90px;

  .sections > section {
    padding: 70px 0;

    &:not(:last-child) {
      border-bottom: 1px solid #e3e5ea;
    }
  }

  @media (min-width: 992px) {
    width: calc(100% - 410px);
  }
`;

const Partner: React.FC<Props> = (props) => {
  const { template } = props;
  const sectionsRef = useRef<Record<string, HTMLElement | null>>({ info: null, review: null, faq: null });
  const [active, setActive] = useState('info');
  const isActive = (target: string) => active === target;
  const activeClass = (target: string) => (isActive(target) ? 'active' : '');
  const focus = (target: string) => {
    if (active === target) return;
    const element = sectionsRef.current[target];
    if (!element) return null;
    const headerOffset = 100;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const keys = Object.keys(sectionsRef.current);
    const handleScroll = () => {
      let offset = [0];
      for (let i = 1; i < keys.length; i++) {
        const element = sectionsRef.current[keys[i]];
        if (!element) return;
        const elementPosition = element.getBoundingClientRect().top;
        offset.push(elementPosition + window.scrollY - 180);
      }
      for (let i = keys.length - 1; i >= 0; i--) {
        if (scrollY >= offset[i]) return setActive(keys[i]);
      }
    };
    const listener = throttle(handleScroll, 100);
    window.addEventListener('scroll', listener, { passive: true });
    return () => window.removeEventListener('scroll', listener);
  }, []);

  return (
    <>
      <Container innerStyle={{ display: 'flex', position: 'relative' }}>
        {template?.Seo.map((s, idx) => (
          <HeadMeta key={idx} {...s} />
        ))}
        {template?.partnerSEOStructuredData?.schema ? (
          <SEOStructuredDataComponent schema={template.partnerSEOStructuredData.schema} />
        ) : null}
        <Wrapper>
          <PartnerBreadcrumb {...props} />
          <PartnerName {...props} focusOn={focus} />
          <StickyContainer>
            <Sticky>
              {({ isSticky, style }) => (
                <SmallTab style={{ ...style, ...(isSticky ? { width: '100%' } : {}) }}>
                  <div onClick={() => focus('info')} className={`item ${activeClass('info')}`}>
                    <h5>파트너 정보</h5>
                  </div>
                  <div onClick={() => focus('review')} className={`item ${activeClass('review')}`}>
                    <h5>후기</h5>
                  </div>
                  <div onClick={() => focus('faq')} className={`item ${activeClass('faq')}`}>
                    <h5>자주 묻는 질문</h5>
                  </div>
                </SmallTab>
              )}
            </Sticky>

            <div className="sections">
              <section ref={(ref) => (sectionsRef.current.info = ref)}>
                <PartnerInfo {...props} />
              </section>
              <section ref={(ref) => (sectionsRef.current.review = ref)}>
                <PartnerReview {...props} />
              </section>
              <section ref={(ref) => (sectionsRef.current.faq = ref)}>
                <PartnerFAQ {...props} />
              </section>
            </div>
          </StickyContainer>
        </Wrapper>
        <PartnerCTA />
      </Container>
      {template?.CrossSelling ? <RelatedServices services={template.CrossSelling.services} /> : null}
    </>
  );
};

export default Partner;
